import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import IntegrationHero from '../../components/integration-hero'
import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import { getCanonicalUrl } from '../../utils/canonical'
import DarkNav from '../../components/dark-nav'
import './zapier.css'

const ZapierIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [activeSection, setActiveSection] = useState('getting-started')

  useEffect(() => {
    // Initialize AOS animations if available
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    // Scroll to top functionality
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)

      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveSection(sectionId)
    }
  }

  const trackEvent = (eventName, properties = {}) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, properties)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier' }
  ]

  const heroContent = {
    title: "DataFlows SMS Zapier Integration",
    subtitle: "Connect your SMS with 6,000+ apps",
    description: "Automate SMS workflows without code. Connect DataFlows SMS with your CRM, e-commerce, support, and marketing tools using Zapier's powerful automation platform.",
    primaryCta: {
      text: "Get Started with Zapier",
      href: "https://zapier.com/apps/dataflows-sms",
      onClick: () => trackEvent('zapier_get_started_clicked')
    },
    secondaryCta: {
      text: "View Documentation",
      href: "#getting-started"
    }
  }

  const navSections = [
    { id: 'getting-started', label: 'Getting Started', icon: '🚀' },
    { id: 'triggers', label: 'Triggers', icon: '⚡' },
    { id: 'actions', label: 'Actions', icon: '🎯' },
    { id: 'use-cases', label: 'Use Cases', icon: '💡' },
    { id: 'best-practices', label: 'Best Practices', icon: '✨' },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: '🔧' }
  ]

  const keyFeatures = [
    {
      icon: "⚡",
      title: "Instant Triggers",
      description: "Real-time webhooks with no polling delays. Get notified instantly when SMS events occur."
    },
    {
      icon: "🤖",
      title: "AI-Powered Analysis",
      description: "Advanced AI for sentiment analysis and automatic message categorisation."
    },
    {
      icon: "📊",
      title: "Dynamic Dropdowns",
      description: "Select sender IDs and contact groups directly in Zapier without copying IDs."
    },
    {
      icon: "🕒",
      title: "Scheduled Sending",
      description: "Schedule SMS for future delivery to reach customers at the perfect time."
    }
  ]

  const triggers = [
    {
      title: "New SMS Received",
      icon: "📩",
      description: "Triggers instantly when your DataFlows virtual number receives an SMS message.",
      useCases: [
        "Auto-reply to customer SMS",
        "Log messages to Google Sheets",
        "Create support tickets from SMS"
      ],
      badge: "Instant",
      link: "/integrations/zapier/triggers/new-sms-received"
    },
    {
      title: "Keyword Matched in SMS",
      icon: "🔍",
      description: "Triggers when an incoming SMS contains a specific keyword or phrase (STOP, HELP, custom commands).",
      useCases: [
        "Handle STOP requests automatically",
        "Customer loyalty programs",
        "Event registration via SMS"
      ],
      badge: "Instant",
      link: "/integrations/zapier/triggers/keyword-matched"
    },
    {
      title: "SMS Delivery Status Changed",
      icon: "✅",
      description: "Triggers when the delivery status of a sent SMS changes (Delivered, Failed, Bounced).",
      useCases: [
        "Alert team on failed deliveries",
        "Update CRM with delivery status",
        "Monitor delivery rates"
      ],
      badge: "Instant",
      link: "/integrations/zapier/triggers/sms-delivery-status"
    },
    {
      title: "Low Balance Alert",
      icon: "💰",
      description: "Triggers when your SMS credit balance drops below a threshold (default: 100 credits).",
      useCases: [
        "Email billing team",
        "Slack notifications",
        "Auto-purchase credits"
      ],
      badge: "Alert",
      link: "/integrations/zapier/triggers/low-balance-alert"
    }
  ]

  const actions = [
    {
      title: "Send SMS",
      icon: "📤",
      description: "Send an SMS message to a single recipient. Optionally schedule for future delivery.",
      useCases: [
        "Welcome SMS for new customers",
        "Order confirmations",
        "Appointment reminders"
      ],
      link: "/integrations/zapier/actions/send-sms"
    },
    {
      title: "Create Contact",
      icon: "👤",
      description: "Create a new contact and add them to a contact group in DataFlows SMS.",
      useCases: [
        "Sync Mailchimp subscribers",
        "Add webinar registrants",
        "E-commerce customer lists"
      ],
      link: "/integrations/zapier/actions/create-contact"
    },
    {
      title: "Categorise SMS With AI",
      icon: "🏷️",
      description: "Use advanced AI to automatically categorise SMS messages into your custom categories.",
      useCases: [
        "Route SMS to right department",
        "Prioritise support tickets",
        "Auto-tag emails by category"
      ],
      badge: "AI",
      link: "/integrations/zapier/actions/categorise-sms-ai"
    },
    {
      title: "Analyse SMS Sentiment With AI",
      icon: "😊",
      description: "Use advanced AI to analyse sentiment of SMS messages (Positive, Negative, Neutral).",
      useCases: [
        "Priority support for angry customers",
        "Happy customer follow-ups",
        "Sentiment dashboard tracking"
      ],
      badge: "AI",
      link: "/integrations/zapier/actions/analyse-sentiment-ai"
    }
  ]

  const realWorldUseCases = [
    {
      category: "E-Commerce & Retail",
      icon: "🛒",
      examples: [
        {
          title: "Order Status Updates",
          description: "Shopify order changes → Send SMS with tracking link",
          apps: "Shopify + DataFlows SMS"
        },
        {
          title: "Abandoned Cart Recovery",
          description: "Abandoned cart → Send SMS reminder with discount code",
          apps: "Shopify + DataFlows SMS"
        },
        {
          title: "Customer Review Requests",
          description: "Positive sentiment → Ask for review",
          apps: "DataFlows SMS + Sentiment AI"
        }
      ]
    },
    {
      category: "Customer Support",
      icon: "💬",
      examples: [
        {
          title: "Intelligent Ticket Routing",
          description: "Categorise SMS → Route to correct Zendesk department",
          apps: "DataFlows SMS + Categorisation + Zendesk"
        },
        {
          title: "VIP Customer Alerts",
          description: "SMS from VIP + negative sentiment → Alert manager",
          apps: "DataFlows SMS + Sentiment + Slack"
        }
      ]
    },
    {
      category: "Healthcare & Appointments",
      icon: "🏥",
      examples: [
        {
          title: "Appointment Reminders",
          description: "Calendar event 24h before → Send SMS reminder",
          apps: "Google Calendar + DataFlows SMS"
        },
        {
          title: "Prescription Refill Requests",
          description: "Keyword REFILL → Auto-reply with refill status",
          apps: "DataFlows SMS + Keyword Match + Airtable"
        }
      ]
    },
    {
      category: "Marketing & Sales",
      icon: "📈",
      examples: [
        {
          title: "Lead Qualification",
          description: "Categorise intent → Update HubSpot with lead score",
          apps: "DataFlows SMS + Categorisation + HubSpot"
        },
        {
          title: "SMS Drip Campaign",
          description: "New lead → Welcome SMS → Day 3: Info → Day 7: Offer",
          apps: "Airtable + DataFlows SMS + Delay"
        }
      ]
    }
  ]

  const bestPractices = [
    {
      category: "Message Quality",
      icon: "✨",
      tips: [
        {
          title: "Personalisation",
          bad: "Your order has shipped",
          good: "Hi Sarah, your order #1234 has shipped!"
        },
        {
          title: "Clear Call-to-Action",
          bad: "Check your email for details",
          good: "Track your package: example.com/track/abc123"
        },
        {
          title: "Character Limits",
          tip: "Keep under 160 chars for single SMS segment to save costs"
        }
      ]
    },
    {
      category: "AI Actions",
      icon: "🤖",
      tips: [
        {
          title: "Provide Good Categories",
          bad: "Type 1, Type 2, Type 3",
          good: "Delivery Issue, Billing Question, Product Inquiry"
        },
        {
          title: "Use Category Descriptions",
          tip: "Help AI understand context: 'package lost, late delivery, tracking not working'"
        },
        {
          title: "Confidence Thresholds",
          tip: "Only act on high confidence (>0.7). Low confidence (<0.6) → route to human"
        }
      ]
    }
  ]

  const faqItems = [
    {
      question: "How do I connect DataFlows SMS to Zapier?",
      answer: "Create a new Zap in Zapier, search for 'DataFlows SMS', and sign in with your API token. You can find your API token at: https://sms.dataflows.com.au/developers/api"
    },
    {
      question: "Are the triggers instant or polling-based?",
      answer: "All DataFlows SMS triggers are instant webhooks with no polling delays. You'll receive notifications in real-time as soon as events occur."
    },
    {
      question: "What AI models power the sentiment and categorisation?",
      answer: "We use advanced AI technology for both sentiment analysis and message categorisation, providing accurate results with confidence scores."
    },
    {
      question: "Can I schedule SMS for future delivery?",
      answer: "Yes! The Send SMS action includes an optional Schedule Time field where you can specify when the message should be sent."
    },
    {
      question: "How do I handle phone number formatting?",
      answer: "Phone numbers must be in E.164 format (+61404235133). Use Zapier's Formatter tool to convert numbers if needed."
    },
    {
      question: "What happens if my account runs out of credits?",
      answer: "The Low Balance Alert trigger will notify you when credits drop below 100. You can set up automatic top-up workflows or email notifications to your billing team."
    }
  ]

  return (
    <div className="zapier-integration-container">
      <DarkNav />
      <Helmet>
        <title>DataFlows SMS Zapier Integration — Connect with 6,000+ Apps</title>
        <meta
          name="description"
          content="Automate SMS workflows with DataFlows SMS and Zapier. Connect with 6,000+ apps including Shopify, HubSpot, Slack, and Google. AI-powered sentiment analysis and categorisation included."
        />
        <meta name="keywords" content="Zapier SMS, SMS automation, Zapier integration, AI sentiment analysis, SMS workflows, business automation" />
        <meta property="og:title" content="DataFlows SMS Zapier Integration" />
        <meta property="og:description" content="Connect DataFlows SMS with 6,000+ apps. Automate SMS workflows with AI-powered analysis, instant triggers, and powerful actions." />
        <meta property="og:image" content="https://dataflows.com.au/zapier_official_logo_icon_167936-200h.png" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Zapier", "item": "https://dataflows.com.au/integrations/zapier" }
          ]
        })}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="zapier-integration-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <IntegrationHero {...heroContent} />

      {/* Navigation Menu */}
      <nav className="zapier-nav-menu" data-aos="fade-down">
        <div className="container">
          <div className="nav-menu-scroll">
            {navSections.map((section) => (
              <button
                key={section.id}
                className={`nav-menu-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-label">{section.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Key Features Section */}
      <section className="zapier-key-features" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why DataFlows SMS + Zapier?</h2>
            <p className="section-description">
              Powerful automation features that make integration seamless
            </p>
          </div>

          <div className="features-grid">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="feature-icon emoji-icon">
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section id="getting-started" className="zapier-section getting-started-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">🚀 Getting Started</h2>
            <p className="section-description">
              Connect DataFlows SMS to Zapier in 3 simple steps
            </p>
          </div>

          <div className="steps-grid">
            <div className="step-card" data-aos="fade-right">
              <div className="step-number">1</div>
              <h3>Get Your API Token</h3>
              <p>Sign up at <a href="https://sms.dataflows.com.au" target="_blank" rel="noopener noreferrer">sms.dataflows.com.au</a> and get your API token from Settings → Developers → API</p>
            </div>

            <div className="step-card" data-aos="fade-right" data-aos-delay="100">
              <div className="step-number">2</div>
              <h3>Create Your First Zap</h3>
              <p>In Zapier, search for "DataFlows SMS" and click "Sign in" when prompted</p>
            </div>

            <div className="step-card" data-aos="fade-right" data-aos-delay="200">
              <div className="step-number">3</div>
              <h3>Start Automating</h3>
              <p>Choose a trigger or action and connect with 6,000+ apps to build powerful workflows</p>
            </div>
          </div>
        </div>
      </section>

      {/* Triggers Section */}
      <section id="triggers" className="zapier-section triggers-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">⚡ Triggers</h2>
            <p className="section-description">
              Events that automatically start your Zaps
            </p>
          </div>

          <div className="triggers-grid">
            {triggers.map((trigger, index) => {
              const CardWrapper = trigger.link ? Link : 'div';
              const cardProps = trigger.link ? { to: trigger.link, style: { textDecoration: 'none', color: 'inherit' } } : {};

              return (
                <CardWrapper key={index} {...cardProps}>
                  <div className="trigger-card" data-aos="fade-up" data-aos-delay={index * 100} style={trigger.link ? { cursor: 'pointer' } : {}}>
                    {trigger.badge && <span className="card-badge">{trigger.badge}</span>}
                    <div className="trigger-icon emoji-icon">{trigger.icon}</div>
                    <h3 className="trigger-title">{trigger.title}</h3>
                    <p className="trigger-description">{trigger.description}</p>
                    <div className="use-cases">
                      <p className="use-cases-label">Use Cases:</p>
                      <ul>
                        {trigger.useCases.map((useCase, i) => (
                          <li key={i}>{useCase}</li>
                        ))}
                      </ul>
                    </div>
                    {trigger.link && <div className="learn-more-indicator">Learn More →</div>}
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Actions Section */}
      <section id="actions" className="zapier-section actions-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">🎯 Actions</h2>
            <p className="section-description">
              Tasks your Zap performs automatically
            </p>
          </div>

          <div className="actions-grid">
            {actions.map((action, index) => {
              const CardWrapper = action.link ? Link : 'div';
              const cardProps = action.link ? { to: action.link, style: { textDecoration: 'none', color: 'inherit' } } : {};

              return (
                <CardWrapper key={index} {...cardProps}>
                  <div className="action-card" data-aos="fade-up" data-aos-delay={index * 100} style={action.link ? { cursor: 'pointer' } : {}}>
                    {action.badge && <span className="card-badge ai-badge">{action.badge}</span>}
                    <div className="action-icon emoji-icon">{action.icon}</div>
                    <h3 className="action-title">{action.title}</h3>
                    <p className="action-description">{action.description}</p>
                    <div className="use-cases">
                      <p className="use-cases-label">Examples:</p>
                      <ul>
                        {action.useCases.map((useCase, i) => (
                          <li key={i}>{useCase}</li>
                        ))}
                      </ul>
                    </div>
                    {action.link && <div className="learn-more-indicator">Learn More →</div>}
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="zapier-section use-cases-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">💡 Real-World Use Cases</h2>
            <p className="section-description">
              See how businesses automate SMS workflows with Zapier
            </p>
          </div>

          {realWorldUseCases.map((category, index) => (
            <div key={index} className="use-case-category" data-aos="fade-up">
              <h3 className="category-title">
                <span className="category-icon">{category.icon}</span>
                {category.category}
              </h3>
              <div className="examples-grid">
                {category.examples.map((example, i) => (
                  <div key={i} className="example-card">
                    <h4>{example.title}</h4>
                    <p className="example-description">{example.description}</p>
                    <p className="example-apps">
                      <strong>Apps:</strong> {example.apps}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Best Practices Section */}
      <section id="best-practices" className="zapier-section best-practices-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">✨ Best Practices</h2>
            <p className="section-description">
              Pro tips for building effective SMS automation workflows
            </p>
          </div>

          {bestPractices.map((practice, index) => (
            <div key={index} className="practice-category" data-aos="fade-up">
              <h3 className="practice-title">
                <span className="practice-icon">{practice.icon}</span>
                {practice.category}
              </h3>
              <div className="tips-grid">
                {practice.tips.map((tip, i) => (
                  <div key={i} className="tip-card">
                    <h4>{tip.title}</h4>
                    {tip.bad && (
                      <p className="comparison">
                        <span className="bad">❌ Bad: {tip.bad}</span>
                        <span className="good">✅ Good: {tip.good}</span>
                      </p>
                    )}
                    {tip.tip && <p className="tip-text">{tip.tip}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Troubleshooting Section */}
      <section id="troubleshooting" className="zapier-section troubleshooting-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">🔧 Troubleshooting</h2>
            <p className="section-description">
              Common issues and their solutions
            </p>
          </div>

          <div className="troubleshooting-grid">
            <div className="issue-card" data-aos="fade-up">
              <h3>Invalid API token error</h3>
              <div className="solution">
                <p><strong>Solutions:</strong></p>
                <ul>
                  <li>Get fresh API token from https://sms.dataflows.com.au/developers/api</li>
                  <li>Copy entire token (no spaces)</li>
                  <li>Reconnect in Zapier</li>
                </ul>
              </div>
            </div>

            <div className="issue-card" data-aos="fade-up" data-aos-delay="100">
              <h3>Phone numbers not in correct format</h3>
              <div className="solution">
                <p><strong>Solutions:</strong></p>
                <ul>
                  <li>Use Zapier Formatter to convert to E.164 format</li>
                  <li>Add country code (+61 for Australia)</li>
                  <li>Remove spaces and special characters</li>
                </ul>
              </div>
            </div>

            <div className="issue-card" data-aos="fade-up" data-aos-delay="200">
              <h3>AI categorisation returning wrong category</h3>
              <div className="solution">
                <p><strong>Solutions:</strong></p>
                <ul>
                  <li>Improve category descriptions with more keywords</li>
                  <li>Use more specific category names</li>
                  <li>Check confidence score - if low (&lt;0.6), route to human</li>
                </ul>
              </div>
            </div>

            <div className="issue-card" data-aos="fade-up" data-aos-delay="300">
              <h3>SMS not sending</h3>
              <div className="solution">
                <p><strong>Solutions:</strong></p>
                <ul>
                  <li>Check account has sufficient credits</li>
                  <li>Verify recipient number is valid</li>
                  <li>Check sender ID is approved</li>
                  <li>Review Zap history for API errors</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="zapier-faq" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>

          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item" data-aos="fade-up" data-aos-delay={index * 50}>
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="zapier-cta" data-aos="fade-up">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Automate Your SMS Workflows?</h2>
            <p>Connect DataFlows SMS with 6,000+ apps and start building powerful automations today.</p>
            <div className="cta-buttons">
              <a
                href="https://zapier.com/apps/dataflows-sms"
                className="btn btn-primary"
                onClick={() => trackEvent('zapier_cta_clicked')}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Started with Zapier
              </a>
              <a
                href="https://sms.dataflows.com.au/register"
                className="btn btn-secondary"
                onClick={() => trackEvent('signup_cta_clicked')}
              >
                Sign Up for DataFlows
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          className="scroll-to-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}

      <Footer />
    </div>
  )
}

export default ZapierIntegration
